import React, { useEffect, useMemo, useState } from 'react';
import PhoneLink from '../components/PhoneLink';
import { useAuth } from '../context/AuthContext';
import { bedService, bookingService } from '../services/api';
import { Bed, Booking } from '../types';
import './Dashboard.css';

type ViewMode = 'list' | 'calendar';

const bedLabel = (b: any) => {
  const bedNumber = b?.bedNumber || b?.bedName || `#${b?.id}`;
  const roomNumber = b?.room?.roomNumber || b?.roomId;
  const floorNumber = b?.room?.floor?.floorNumber || b?.floorId;
  return `F${floorNumber ?? '-'} / R${roomNumber ?? '-'} / Bed ${bedNumber}`;
};

const bookingBedLabel = (bk: any) => {
  if (bk?.bed) return bedLabel(bk.bed);
  return `Bed ${bk?.bedId}`;
};

const Bookings: React.FC = () => {
  const { hostel } = useAuth();
  const [loading, setLoading] = useState(true);
  const [beds, setBeds] = useState<Bed[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [status, setStatus] = useState<'ALL' | Booking['status']>('ALL');
  const [view, setView] = useState<ViewMode>('list');

  const [form, setForm] = useState({
    bedId: '',
    userId: '',
    startDate: '',
    endDate: '',
    guestName: '',
    guestPhoneNumber: '',
    notes: '',
  });

  const load = async () => {
    if (!hostel?.id) return;
    setLoading(true);
    try {
      const [bs, bk] = await Promise.all([
        bedService.getBedsByHostel(hostel.id),
        bookingService.getBookingsByHostel(hostel.id, status === 'ALL' ? 'ALL' : status),
      ]);
      setBeds(bs);
      setBookings(bk);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostel?.id, status]);

  const availableBeds = useMemo(() => beds.filter((b) => b.bedStatus === 'AVAILABLE'), [beds]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    await bookingService.createBooking({
      hostelId: hostel.id,
      bedId: Number(form.bedId),
      userId: form.userId ? Number(form.userId) : undefined,
      startDate: form.startDate,
      endDate: form.endDate,
      guestName: form.guestName || undefined,
      guestPhoneNumber: form.guestPhoneNumber || undefined,
      notes: form.notes || undefined,
    } as any);
    setForm({ bedId: '', userId: '', startDate: '', endDate: '', guestName: '', guestPhoneNumber: '', notes: '' });
    await load();
  };

  const cancel = async (b: Booking) => {
    const ok = window.confirm(`Cancel booking #${b.id}?`);
    if (!ok) return;
    await bookingService.cancelBooking(b.id);
    await load();
  };

  const checkin = async (b: any) => {
    const ok = window.confirm(`Convert booking #${b.id} to check-in?`);
    if (!ok) return;
    await bookingService.checkinBooking(b.id);
    await load();
  };

  const calendarDays = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, []);

  const bookingsForDay = (day: Date) => {
    const dayStart = new Date(day);
    const dayEnd = new Date(day);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return (bookings as any[]).filter((b) => {
      const s = new Date((b as any).startDate);
      const e = new Date((b as any).endDate);
      return s < dayEnd && dayStart < e;
    });
  };

  if (!hostel) return <div className="loading-container">Loading...</div>;

  return (
    <div className="dashboard-container">
      <h1>Bookings / Reservations</h1>

      <div className="form-container">
        <h2>Create Booking</h2>
        <form onSubmit={create}>
          <div className="form-row">
            <div className="form-group">
              <label>Bed *</label>
              <select value={form.bedId} onChange={(e) => setForm({ ...form, bedId: e.target.value })} required>
                <option value="">Select an available bed</option>
                {availableBeds.map((b) => (
                  <option key={b.id} value={String(b.id)}>
                    {bedLabel((b as any))}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>User ID (optional)</label>
              <input value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Start *</label>
              <input
                type="datetime-local"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>End *</label>
              <input
                type="datetime-local"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Guest name</label>
              <input value={form.guestName} onChange={(e) => setForm({ ...form, guestName: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Guest phone</label>
              <input
                value={form.guestPhoneNumber}
                onChange={(e) => setForm({ ...form, guestPhoneNumber: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>

          <button className="btn btn-success" type="submit" disabled={loading}>
            Create Booking
          </button>
        </form>
      </div>

      <div className="filter-section">
        <div className="filter-controls">
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}>
            <option value="ALL">All</option>
            <option value="RESERVED">Reserved</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="CHECKED_IN">Checked in</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <select value={view} onChange={(e) => setView(e.target.value as ViewMode)}>
            <option value="list">List</option>
            <option value="calendar">Calendar</option>
          </select>
          <button className="btn btn-primary" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">Loading...</div>
      ) : view === 'calendar' ? (
        <div className="list-container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              gap: '0.75rem',
            }}
          >
            {calendarDays.map((d) => {
              const items = bookingsForDay(d);
              return (
                <div
                  key={d.toISOString()}
                  className="list-item"
                  style={{ minHeight: 140, borderLeft: items.length ? '4px solid #667eea' : '4px solid #eee' }}
                >
                  <div className="item-header">
                    <h3 style={{ fontSize: '1rem' }}>
                      {d.toLocaleDateString(undefined, { weekday: 'short' })} {d.getDate()}
                    </h3>
                    <span className="badge">{items.length}</span>
                  </div>
                  {items.slice(0, 4).map((b: any) => (
                    <div key={b.id} style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                      <strong>#{b.id}</strong> {bookingBedLabel(b)}
                    </div>
                  ))}
                  {items.length > 4 && <div className="text-muted">+{items.length - 4} more…</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="list-container">
          {bookings.length === 0 ? (
            <p>No bookings found.</p>
          ) : (
            bookings.map((b) => (
              <div key={b.id} className="list-item">
                <div className="item-header">
                  <h3>Booking #{b.id}</h3>
                  <span className="badge">{b.status}</span>
                </div>
                <p>
                  <strong>Bed:</strong> {bookingBedLabel(b as any)} | <strong>User:</strong> {(b as any).user?.username || b.userId || '—'}
                </p>
                <p>
                  <strong>Guest:</strong> {b.guestName || '—'}{' '}
                  {b.guestPhoneNumber ? (
                    <>
                      (<PhoneLink phone={b.guestPhoneNumber} />)
                    </>
                  ) : (
                    ''
                  )}
                </p>
                <p>
                  <strong>From:</strong> {new Date(b.startDate).toLocaleString()} — <strong>To:</strong>{' '}
                  {new Date(b.endDate).toLocaleString()}
                </p>
                {b.notes && <p className="text-muted">{b.notes}</p>}
                {b.status === 'RESERVED' && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-danger" onClick={() => cancel(b)}>
                      Cancel
                    </button>
                    {(b as any).userId && (
                      <button className="btn btn-success" onClick={() => checkin(b)}>
                        Convert → Check-in
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Bookings;

